<template>
  <v-container class="pr-0 pl-0">
    <v-combobox
      full-width
      :value="endTime"
      :items="times"
      outlined
      dense
      item-text="text"
      item-value="value"
      hide-details
      @change="onEndDateChange"
      @blur="onBlur"
    >
      <template #append>
        <v-icon
          class="ml-0 custom-append-icon"
          style="margin-top: 2px"
          color="rgba(0, 0, 0, 0.54)"
          size="19"
          >mdi-clock-outline</v-icon
        >
      </template>
      <template v-slot:item="{ item }">
        <span style="font-size: 13px">{{ item.scheduleTime }}</span>
      </template>
    </v-combobox>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      selectedTime: new Date(),
      times: [],
    };
  },
  props: {
    value: {
      type: [String, Date],
      default: () => new Date(),
    },
    minimumDate: {
      type: [String, Date],
      default: () => new Date(),
    },
  },
  computed: {
    locale() {
      return this.$i18n.locale;
    },
    dateFormat() {
      return this.locale !== "ja" ? "hh:mm" : "HH:mm";
    },
    endTime() {
      return this.convertDateToHHMM(this.selectedTime || new Date());
    },
  },
  watch: {
    minimumDate(value) {
      this.generateTimeOptions(value);
    },
    value(value) {
      this.selectedTime = value || new Date();
    },
  },
  mounted() {
    this.selectedTime = this.value;
    this.generateTimeOptions(this.minimumDate);
  },
  methods: {
    onBlur,
    getNext24Dates,
    onEndDateChange,
    convertDateToHHMM,
    convertTimeStringToDate,
    generateTimeOptions,
    isValidTimeString,
  },
};

function generateTimeOptions(minimumDate) {
  const nextDates = this.getNext24Dates(new Date(minimumDate));
  let actulaMin = minimumDate.getMinutes();
  this.times = [];
  let tempTimes = [];
  let nextMin = 0;
  let increaseMin = 30;
  nextDates.forEach((date, index) => {
    const hhMM = this.convertDateToHHMM(date);
    let minutes = date.getMinutes();
    if (index === 0 && minutes === 0) {
      increaseMin = 60;
    }
    nextMin = increaseMin - actulaMin;
    increaseMin += 30;
    const timeString = `${hhMM} (${convertMinutesToHHMM(nextMin)})`;
    const obj = {
      text: `${hhMM}`,
      value: `${hhMM}`,
      actualDate: date,
      scheduleTime: timeString,
    };
    tempTimes.push(obj);
  });
  this.times = tempTimes;
}

function getNext24Dates(startDate) {
  let currentDate = new Date(startDate);
  let nextDates = [];
  for (let i = 0; i < 24; i++) {
    currentDate.setMinutes(Math.round(currentDate.getMinutes() / 30) * 30);
    currentDate.setMinutes(currentDate.getMinutes() + 30);
    nextDates.push(new Date(currentDate));
  }

  return nextDates;
}

function isValidTimeString(timeString) {
  let timeRegex;
  if (this.locale !== "ja") {
    timeRegex = /^(0?[1-9]|1[0-2]):[0-5][0-9] (AM|PM)$/i;
  } else {
    timeRegex = /^(?:0?[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
  }
  return timeRegex.test(timeString);
}

function onBlur(event) {
  if (event.target.value !== "") {
    if (!this.isValidTimeString(event.target.value)) {
      resetSelectedTime.call(this);
    }
  } else {
    resetSelectedTime.call(this);
  }
}

function resetSelectedTime() {
  this.selectedTime = null;
  this.$nextTick(() => {
    this.selectedTime = this.value;
  });
}

function onEndDateChange(event) {
  const date = new Date(event.actualDate);

  if (isNaN(date.getTime()) && this.isValidTimeString(event)) {
    let newDate = this.convertTimeStringToDate(event);

    // If the new date is less than the minimum date, emit the next date
    if (new Date(this.minimumDate).getTime() >= newDate.getTime()) {
      newDate = new Date(newDate);
      newDate.setDate(newDate.getDate() + 1);
    }
    this.$emit("change", newDate);
  } else {
    if (this.isValidTimeString(event)) {
      let [hours, minutes] = time.split(":");
      let newDate = new Date(this.selectedTime);
      newDate.setHours(hours);
      newDate.setMinutes(minutes);

      this.$emit("change", !isNaN(newDate.getTime()) ? newDate : new Date());
    } else if (!isNaN(date.getTime())) {
      this.$emit("change", !isNaN(date.getTime()) ? date : new Date());
    }
  }
}

function convertTimeStringToDate(timeString) {
  let [time, ampm] = timeString.split(" ");
  let [hours, minutes] = time.split(":");

  if (ampm.toLowerCase() === "pm" && hours !== "12") {
    hours = String(Number(hours) + 12);
  } else if (ampm.toLowerCase() === "am" && hours === "12") {
    hours = "00";
  }

  let date = new Date();
  date.setHours(Number(hours));
  date.setMinutes(Number(minutes));

  return date;
}

function convertDateToHHMM(now) {
  const hours = now.getHours();
  const minutes = (now.getMinutes() < 10 ? "0" : "") + now.getMinutes();
  let time;

  if (this.locale !== "ja") {
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12; // Handle midnight (0 hours)
    time = `${formattedHours}:${minutes} ${ampm}`;
  } else {
    time = `${hours}:${minutes}`;
  }
  return time;
}

function convertMinutesToHHMM(minutes) {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  const formattedMinutes =
    remainingMinutes < 10 ? "0" + remainingMinutes : remainingMinutes;
  if (hours === 0) {
    return `${formattedMinutes}m`;
  }
  return `${hours}h ${formattedMinutes}m`;
}
</script>

<style scoped>
::v-deep .v-list-item--dense,
.v-list--dense .v-list-item {
  min-width: 35px !important;
  padding-right: 10px !important;
}
::v-deep .v-autocomplete.v-select.v-input--is-focused input {
  min-width: 0px !important;
}

::v-deep .v-select.v-text-field input {
  font-size: 13px !important;
}

::v-deep .v-text-field--outlined.v-input--is-focused fieldset,
.v-text-field--outlined.v-input--has-state fieldset {
  /* border-color: rgba(0, 0, 0, 0.42) !important; */
  border: 1px solid rgba(0, 0, 0, 0.42) !important;
  border-bottom: 2px solid #e3165b !important;
}

::v-deep .v-text-field .v-input__append-inner {
  padding-left: 0px !important;
}

::v-deep
  .v-text-field--outlined.v-input--dense.v-text-field--outlined
  > .v-input__control
  > .v-input__slot {
  padding-right: 6px;
  padding-left: 6px;
}

.custom-append-icon.v-icon {
  cursor: pointer;
}
</style>
